import { useState, useEffect, useCallback, useId } from 'react'
import * as Sentry from '@sentry/tanstackstart-react'
import FocusLock from 'react-focus-lock'
import { X as CloseIcon, ArrowLeft as ArrowLeftIcon, Loader as SpinnerIcon } from 'lucide-react'
import { api, schoolApi, type DetectedDeviceResponse, type StudentResponse } from '../../lib/api'
import { useActiveJobsOptional, type ActiveJob } from '../../context/ActiveJobsContext'
import { useDetectedDevices } from '../../hooks/useDeviceQuery'
import { useCommandStatus } from '../../hooks/useCommandQuery'
import { useCommandSSE } from '../../hooks/useSSE'
import { useHelperStatus } from '../../hooks/useHelperStatus'
import { useQuery } from '@tanstack/react-query'
import { queries } from '../../lib/queries'
import { polling } from '../../lib/polling'

const AppleIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
  </svg>
)

const AndroidIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M6 18c0 .55.45 1 1 1h1v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h2v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h1c.55 0 1-.45 1-1V8H6v10zM3.5 8C2.67 8 2 8.67 2 9.5v7c0 .83.67 1.5 1.5 1.5S5 17.33 5 16.5v-7C5 8.67 4.33 8 3.5 8zm17 0c-.83 0-1.5.67-1.5 1.5v7c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5v-7c0-.83-.67-1.5-1.5-1.5zm-4.97-5.84l1.3-1.3c.2-.2.2-.51 0-.71-.2-.2-.51-.2-.71 0l-1.48 1.48C13.85 1.23 12.95 1 12 1c-.96 0-1.86.23-2.66.63L7.85.15c-.2-.2-.51-.2-.71 0-.2.2-.2.51 0 .71l1.31 1.31C6.97 3.26 6 5.01 6 7h12c0-1.99-.97-3.75-2.47-4.84zM10 5H9V4h1v1zm5 0h-1V4h1v1z"/>
  </svg>
)

import SelectStudent from './SelectStudent'
import SelectDevice from './SelectDevice'
import PreflightChecklist from './PreflightChecklist'
import AndroidPrepChecklist from './AndroidPrepChecklist'
import AndroidVpnConfirm from './AndroidVpnConfirm'
import OperationProgress from './OperationProgress'
import PostRestoreSteps from './PostRestoreSteps'
import PostOperationChecklist from './PostOperationChecklist'

export type OperationStep =
  // Install-only
  | 'select_student'
  | 'select_platform'
  | 'select_device'
  // Android-only
  | 'android_prep'
  | 'android_vpn_confirm'
  // Uninstall-only
  | 'confirm_uninstall'
  // Shared
  | 'preflight'
  | 'progress'
  // Install post-restore (iOS only)
  | 'select_wifi'
  | 'partial_setup'
  | 'enroll'
  | 'verify_enrollment'
  // Shared terminal
  | 'done'

type DevicePlatform = 'ios' | 'android'

interface DeviceOperationModalProps {
  mode: 'install' | 'uninstall'
  onClose: () => void
  onComplete?: () => void
  existingJob?: ActiveJob | null
  preSelectedStudentId?: string
  /** For uninstall: the device to remove */
  deviceId?: string
  deviceName?: string
  /** For uninstall: the device platform (determines which removal API to call) */
  devicePlatform?: 'ios' | 'android'
  /** School context — when set, uses school-scoped APIs */
  schoolId?: string
  /** School enrollment — the membership to enroll (school install only) */
  schoolMembershipId?: string
  /** School install — the detected device UDID (school install skips device selection) */
  udid?: string
}

const STEP_TITLES: Partial<Record<OperationStep, string>> = {
  select_student: 'Which child?',
  select_platform: 'Device Type',
  select_device: 'Select Device',
  android_prep: 'Prepare Android Phone',
  android_vpn_confirm: 'Allow VPN',
  confirm_uninstall: 'Remove Unbreakable',
  preflight: 'Before We Start',
  progress: 'Working...',
  select_wifi: 'Connect to WiFi',
  partial_setup: 'Continue Setup',
  enroll: 'Enroll Device',
  verify_enrollment: 'Verifying Enrollment',
  done: 'Complete',
}

export default function DeviceOperationModal({
  mode,
  onClose,
  onComplete,
  existingJob,
  preSelectedStudentId,
  deviceId: uninstallDeviceId,
  deviceName,
  devicePlatform,
  schoolId,
  schoolMembershipId,
  udid: schoolUdid,
}: DeviceOperationModalProps) {
  const dialogTitleId = useId()
  const { addJob, updateJob } = useActiveJobsOptional()
  const isSchool = !!schoolId

  // Fetch students for family install mode only (school handles student selection externally)
  const { data: devicesListData } = useQuery({
    queryKey: queries.devices.list(),
    queryFn: () => api.getDevices(),
    enabled: mode === 'install' && !isSchool,
  })
  const familyStudents: StudentResponse[] = devicesListData?.students ?? []
  const hasMultipleStudents = familyStudents.length >= 2

  // Determine initial step
  const getInitialStep = (): OperationStep => {
    if (existingJob?.currentStep) return existingJob.currentStep as OperationStep
    if (mode === 'uninstall') return 'confirm_uninstall'
    // School install: student + device already selected, skip to preflight (iOS) or android_prep (Android)
    if (isSchool) return devicePlatform === 'android' ? 'android_prep' : 'preflight'
    if (preSelectedStudentId) return 'select_platform'
    if (hasMultipleStudents) return 'select_student'
    return 'select_platform'
  }

  const [currentStep, setCurrentStep] = useState<OperationStep>(getInitialStep())
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(preSelectedStudentId ?? null)
  const [selectedDevice, setSelectedDevice] = useState<DetectedDeviceResponse | null>(null)
  const [selectedPlatform, setSelectedPlatform] = useState<DevicePlatform | null>(null)
  const [commandId, setCommandId] = useState<string | null>(existingJob?.commandId || null)
  const [error, setError] = useState<string | null>(null)
  const [isStarting, setIsStarting] = useState(false)

  // Auto-assign student for single-kid families
  useEffect(() => {
    if (mode === 'install' && !selectedStudentId && familyStudents.length === 1) {
      setSelectedStudentId(familyStudents[0].id)
    }
  }, [familyStudents, selectedStudentId, mode])

  // Fix initial step when student data loads
  useEffect(() => {
    if (mode === 'install' && currentStep === 'select_platform' && !preSelectedStudentId && hasMultipleStudents && !selectedStudentId) {
      setCurrentStep('select_student')
    }
  }, [hasMultipleStudents, preSelectedStudentId, selectedStudentId, currentStep, mode])

  // Persist step to job
  useEffect(() => {
    if (commandId && currentStep !== 'select_device' && currentStep !== 'select_student' && currentStep !== 'select_platform' && currentStep !== 'android_prep') {
      updateJob(commandId, { currentStep })
    }
  }, [currentStep, commandId, updateJob])

  // Detected devices for family install only (school skips device selection)
  const { data: detectedDevicesData, isLoading: isLoadingDevices } = useDetectedDevices({
    enabled: mode === 'install' && !isSchool && (currentStep === 'select_device' || currentStep === 'select_platform'),
  })
  const detectedDevices = detectedDevicesData?.devices || []
  const { data: helperStatus } = useHelperStatus({ enabled: mode === 'install' })
  const helperReady = helperStatus?.running === true

  // Poll command status during all post-restore steps (not just verify_enrollment)
  // The helper works in the background while the user clicks through manual steps,
  // so the command may complete before the user reaches verify_enrollment
  const isPostRestoreStep = ['select_wifi', 'partial_setup', 'enroll', 'verify_enrollment'].includes(currentStep)
  const { isConnected: sseConnected } = useCommandSSE(commandId || '', {
    enabled: isPostRestoreStep && !!commandId,
  })
  const { data: commandData } = useCommandStatus(
    isPostRestoreStep && commandId ? commandId : '',
    { refetchInterval: sseConnected ? polling.commandWithSSE : polling.commandFallback, schoolId }
  )

  // Auto-advance to done when command completes (from any post-restore step)
  useEffect(() => {
    if (!isPostRestoreStep || !commandData) return
    if (commandData.status === 'completed') {
      setCurrentStep('done')
    } else if (commandData.status === 'failed') {
      setError(commandData.error || 'Enrollment verification failed')
    }
  }, [commandData, currentStep, isPostRestoreStep])

  // Start installation (called from PreflightChecklist or AndroidPrepChecklist flow)
  const handleStartInstall = useCallback(async () => {
    if (!helperReady) {
      setError('Open the Helper app and reconnect the device before starting installation.')
      return
    }

    setIsStarting(true)
    setError(null)
    try {
      let responseCommandId: string
      let jobUdid: string
      let jobDeviceName: string | undefined
      let jobType: 'supervise' | 'android_setup' = 'supervise'

      if (isSchool && schoolId && schoolMembershipId && schoolUdid) {
        // School enrollment
        const effectivePlatform = selectedPlatform || devicePlatform
        if (effectivePlatform === 'android') {
          const response = await schoolApi.androidSetupDevice(schoolId, schoolUdid, schoolMembershipId)
          responseCommandId = response.commandId
          jobUdid = schoolUdid
          jobDeviceName = deviceName || response.device?.name || undefined
          jobType = 'android_setup'
        } else {
          const response = await schoolApi.superviseDetectedDevice(schoolId, schoolUdid, schoolMembershipId)
          responseCommandId = response.commandId
          jobUdid = schoolUdid
          jobDeviceName = deviceName || response.device?.name || undefined
        }
      } else if (selectedDevice && selectedPlatform === 'android') {
        // Android family setup
        const response = await api.androidSetupDevice(selectedDevice.udid, selectedStudentId ?? undefined)
        responseCommandId = response.commandId
        jobUdid = selectedDevice.udid
        jobDeviceName = selectedDevice.name || undefined
        jobType = 'android_setup'
      } else if (selectedDevice) {
        // iOS family enrollment
        const response = await api.superviseDetectedDevice(selectedDevice.udid, selectedStudentId ?? undefined)
        responseCommandId = response.commandId
        jobUdid = selectedDevice.udid
        jobDeviceName = selectedDevice.name || undefined
      } else {
        return
      }

      setCommandId(responseCommandId)
      addJob({
        commandId: responseCommandId,
        type: jobType,
        deviceId: null,
        deviceName: jobDeviceName,
        udid: jobUdid,
        createdAt: new Date().toISOString(),
        currentStep: 'progress',
      })
      setCurrentStep('progress')
    } catch (err) {
      Sentry.captureException(err)
      const msg = err instanceof Error ? err.message : 'Failed to start installation'
      setError(msg.includes('already exists') || msg.includes('DUPLICATE_COMMAND')
        ? 'An installation is already in progress for this device.'
        : msg
      )
    } finally {
      setIsStarting(false)
    }
  }, [helperReady, isSchool, schoolId, schoolMembershipId, schoolUdid, selectedDevice, selectedStudentId, selectedPlatform, deviceName, addJob])

  // Start uninstallation (called from PreflightChecklist)
  const handleStartUninstall = useCallback(async () => {
    if (!uninstallDeviceId) return
    setIsStarting(true)
    setError(null)
    try {
      let responseCommandId: string
      let responseUdid: string | null

      if (isSchool && schoolId) {
        if (devicePlatform === 'android') {
          const response = await schoolApi.androidRemoveDevice(schoolId, uninstallDeviceId)
          responseCommandId = response.commandId
          responseUdid = response.device?.nanomdmDeviceId ?? null
        } else {
          const response = await schoolApi.removeSupervision(schoolId, uninstallDeviceId)
          responseCommandId = response.commandId
          responseUdid = response.device?.nanomdmDeviceId ?? null
        }
      } else if (devicePlatform === 'android') {
        const response = await api.androidRemoveDevice(uninstallDeviceId)
        responseCommandId = response.commandId
        responseUdid = response.device.nanomdmDeviceId
      } else {
        const response = await api.removeSupervision(uninstallDeviceId)
        responseCommandId = response.commandId
        responseUdid = response.device.nanomdmDeviceId
      }

      setCommandId(responseCommandId)
      addJob({
        commandId: responseCommandId,
        type: devicePlatform === 'android' ? 'android_remove' : 'remove_supervision',
        deviceId: uninstallDeviceId,
        deviceName: deviceName,
        udid: responseUdid,
        createdAt: new Date().toISOString(),
        currentStep: 'progress',
      })
      setCurrentStep('progress')
    } catch (err) {
      Sentry.captureException(err)
      setError(err instanceof Error ? err.message : 'Failed to start removal')
    } finally {
      setIsStarting(false)
    }
  }, [isSchool, schoolId, uninstallDeviceId, deviceName, devicePlatform, addJob])

  // Defensive: if Android somehow reaches the preflight step, redirect
  useEffect(() => {
    if (currentStep === 'preflight' && (selectedPlatform === 'android' || devicePlatform === 'android')) {
      if (mode === 'install') handleStartInstall()
      else handleStartUninstall()
    }
  }, [currentStep, selectedPlatform, devicePlatform, mode, handleStartInstall, handleStartUninstall])

  // Escape to close (unless in progress)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && currentStep !== 'progress' && currentStep !== 'verify_enrollment') {
        onClose()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose, currentStep])

  // Back navigation
  const canGoBack = mode === 'install' && ['select_platform', 'select_device', 'preflight', 'android_prep'].includes(currentStep) && (currentStep !== 'select_platform' || hasMultipleStudents)

  const handleBack = () => {
    if (currentStep === 'preflight') {
      setCurrentStep('select_device')
    } else if (currentStep === 'android_prep') {
      setCurrentStep('select_device')
    } else if (currentStep === 'select_device') {
      setCurrentStep('select_platform')
    } else if (currentStep === 'select_platform' && hasMultipleStudents) {
      setCurrentStep('select_student')
    }
  }

  const handleComplete = onComplete || onClose

  // Render step content
  const renderContent = () => {
    switch (currentStep) {
      case 'select_student':
        return (
          <SelectStudent
            students={familyStudents}
            onSelect={(id) => {
              setSelectedStudentId(id)
              setCurrentStep('select_platform')
            }}
          />
        )

      case 'select_platform':
        return (
          <div className="space-y-4">
            <p className="text-gray-600 text-center mb-6">
              What type of device are you adding?
            </p>
            <button
              onClick={() => { setSelectedPlatform('ios'); setCurrentStep('select_device') }}
              className="w-full bg-white border-2 border-gray-200 rounded-xl p-4 hover:border-brand hover:shadow-md transition-all text-left"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0 text-gray-800">
                  <AppleIcon />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">iPhone / iPad</h3>
                  <p className="text-sm text-gray-500">iOS device</p>
                </div>
              </div>
            </button>
            <button
              onClick={() => { setSelectedPlatform('android'); setCurrentStep('select_device') }}
              className="w-full bg-white border-2 border-gray-200 rounded-xl p-4 hover:border-brand hover:shadow-md transition-all text-left"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0 text-gray-800">
                  <AndroidIcon />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">Android</h3>
                  <p className="text-sm text-gray-500">Samsung, Pixel, Xiaomi, etc.</p>
                </div>
              </div>
            </button>
          </div>
        )

      case 'select_device':
        return (
          <SelectDevice
            devices={detectedDevices}
            isLoading={isLoadingDevices}
            platform={selectedPlatform ?? undefined}
            onSelect={(device) => {
              setSelectedDevice(device)
              if (selectedPlatform === 'android') {
                setCurrentStep('android_prep')
              } else {
                setCurrentStep('preflight')
              }
            }}
          />
        )

      case 'confirm_uninstall':
        return (
          <div className="space-y-5">
            {devicePlatform === 'android' ? (
              <p className="text-sm text-gray-600">
                This removes Device Owner and uninstalls the Unbreakable app from <strong>{deviceName || 'this device'}</strong>. All restrictions are cleared and the phone returns to normal. Keep the phone plugged in throughout. Takes about a minute.
              </p>
            ) : (
              <>
                <p className="text-sm text-gray-600">
                  The phone will restart and return to a stock, unmanaged state. All restrictions and policies on <strong>{deviceName || 'this device'}</strong> will be cleared. Keep the iPhone plugged in throughout.
                </p>
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-2">
                  <div className="text-xs font-medium text-amber-900 uppercase tracking-wide">Next: one task on the iPhone</div>
                  <p className="text-sm text-amber-900">
                    You&apos;ll temporarily turn off <strong>Stolen Device Protection</strong> and <strong>Find My iPhone</strong>. We walk you through it on the next screen. Takes about a minute.
                  </p>
                </div>
              </>
            )}
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (devicePlatform === 'android') {
                    // Android: skip preflight (no iOS security settings to disable), go straight to uninstall
                    handleStartUninstall()
                  } else {
                    setCurrentStep('preflight')
                  }
                }}
                className="flex-1 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-medium"
              >
                Continue
              </button>
            </div>
          </div>
        )

      case 'android_prep':
        return (
          <AndroidPrepChecklist
            deviceModel={selectedDevice?.model ?? null}
            helperReady={helperReady}
            onReady={handleStartInstall}
          />
        )

      case 'android_vpn_confirm':
        return (
          <AndroidVpnConfirm onConfirm={() => setCurrentStep('done')} />
        )

      case 'preflight':
        return (selectedPlatform === 'android' || devicePlatform === 'android') ? null : (
          <PreflightChecklist
            mode={mode}
            deviceName={deviceName || selectedDevice?.name || undefined}
            onConfirm={mode === 'install' ? handleStartInstall : handleStartUninstall}
            isLoading={isStarting}
            error={error}
            helperReady={mode === 'install' ? helperReady : true}
          />
        )

      case 'progress':
        return commandId ? (
          <OperationProgress
            commandId={commandId}
            mode={
              mode === 'uninstall' && devicePlatform === 'android' ? 'android_remove'
              : (selectedPlatform === 'android' || devicePlatform === 'android') ? 'android_setup'
              : mode
            }
            schoolId={schoolId}
            onComplete={
              selectedPlatform === 'android' || devicePlatform === 'android'
                ? () => setCurrentStep(mode === 'install' ? 'android_vpn_confirm' : 'done')
                : handleComplete
            }
            onError={(msg) => setError(msg)}
            onPostRestoreReady={mode === 'install' && selectedPlatform !== 'android' && devicePlatform !== 'android' ? () => setCurrentStep('select_wifi') : undefined}
          />
        ) : null

      case 'select_wifi':
      case 'partial_setup':
      case 'enroll':
        return (
          <PostRestoreSteps
            step={currentStep}
            onNext={(next) => setCurrentStep(next)}
          />
        )

      case 'verify_enrollment':
        return (
          <div className="text-center space-y-6">
            <SpinnerIcon size={48} className="animate-spin mx-auto text-brand" />
            <div>
              <p className="text-gray-900 font-medium">Verifying enrollment...</p>
              <p className="text-sm text-gray-500 mt-2">
                Please wait while we confirm your device is enrolled.
              </p>
            </div>
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}
          </div>
        )

      case 'done': {
        const isAndroid = selectedPlatform === 'android' || devicePlatform === 'android'
        if (!isAndroid) {
          return <PostOperationChecklist onDone={handleComplete} />
        }

        const resolvedDeviceName = deviceName || selectedDevice?.name || null
        const studentName = selectedStudentId
          ? familyStudents.find(s => s.id === selectedStudentId)?.name
          : undefined

        return (
          <div className="text-center space-y-5">
            <div className="flex flex-col items-center gap-3">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
              </div>
              <div>
                <p className="text-lg font-medium text-green-600">
                  {mode === 'install' ? 'Device set up successfully!' : 'Unbreakable removed!'}
                </p>
                {resolvedDeviceName && (
                  <p className="text-sm text-gray-500 mt-1">
                    {resolvedDeviceName}{studentName ? ` · ${studentName}'s device` : ''}
                  </p>
                )}
              </div>
            </div>

            {mode === 'install' && (
              <div className="bg-emerald-50 border-2 border-emerald-300 rounded-xl p-4 sm:p-5 text-left">
                <div className="flex items-start gap-3 mb-4">
                  <div className="shrink-0 mt-0.5">
                    <svg className="w-6 h-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M12 3a9 9 0 100 18 9 9 0 000-18z" /></svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-base font-semibold text-emerald-900 mb-3">
                      You can now re-add accounts:
                    </h3>
                    <div className="space-y-2.5">
                      <div className="flex items-start gap-3 bg-white/60 rounded-lg p-2.5">
                        <span className="shrink-0 w-6 h-6 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs font-bold">1</span>
                        <div>
                          <p className="text-sm font-semibold text-emerald-900">Sign back into Google</p>
                          <p className="text-xs text-emerald-700">
                            <strong>Settings &rarr; Accounts &rarr; Add Account &rarr; Google</strong>
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 bg-white/60 rounded-lg p-2.5">
                        <span className="shrink-0 w-6 h-6 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs font-bold">2</span>
                        <div>
                          <p className="text-sm font-semibold text-emerald-900">Re-add any other accounts</p>
                          <p className="text-xs text-emerald-700">
                            Samsung, WhatsApp, or any others that were removed before setup
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {mode === 'uninstall' && (
              <p className="text-sm text-gray-500">
                All restrictions and the Unbreakable app have been removed from this device.
              </p>
            )}

            <button
              onClick={handleComplete}
              className="w-full py-3 text-sm font-medium text-white bg-emerald-600 rounded-xl hover:bg-emerald-700 active:bg-emerald-800 transition-colors"
            >
              {mode === 'install' ? "Done - I\u2019ve added them back" : 'Done'}
            </button>
          </div>
        )
      }
    }
  }

  const isProgressStep = currentStep === 'progress' || currentStep === 'verify_enrollment'
  const title = STEP_TITLES[currentStep] || (mode === 'install' ? 'Install Unbreakable' : 'Remove Unbreakable')

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isProgressStep) {
          onClose()
        }
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby={dialogTitleId}
    >
      <FocusLock returnFocus>
        <div
          className={`bg-white rounded-2xl shadow-xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col ${
            isProgressStep ? 'max-w-2xl' : 'max-w-xl'
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex shrink-0 items-center justify-between px-6 pt-5 pb-2">
            <div className="flex items-center gap-3">
              {canGoBack && (
                <button
                  onClick={handleBack}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  aria-label="Go back"
                >
                  <ArrowLeftIcon size={20} />
                </button>
              )}
              <h2 id={dialogTitleId} className="text-lg font-semibold text-gray-900">
                {title}
              </h2>
            </div>
            {!isProgressStep && (
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Close"
              >
                <CloseIcon size={20} />
              </button>
            )}
          </div>

          {/* Content */}
          <div className="px-6 pt-4 pb-6 flex-1 min-h-0 flex flex-col overflow-y-auto">
            {renderContent()}
          </div>
        </div>
      </FocusLock>
    </div>
  )
}
